import { showError } from "@/modules/error/errorStream.ts";
import { auth0Realm } from "@/modules/login/auth0/auth0Config.ts";
import { Auth0Exception, Auth0LoginException, Auth0SignupException } from "@/modules/login/auth0/Auth0Exception.ts";
import { webAuthAtom } from "@/modules/login/auth0/auth0Loader.ts";
import type { AuthProvider, SocialLoginId } from "@/modules/login/authProvider.ts";
import { getFullUrl } from "@/modules/main/urls.ts";
import type { WebAuth } from "auth0-js";
import { atom } from "jotai/vanilla";
import invariant from "tiny-invariant";

async function auth0Login(webAuth: WebAuth, username: string, password: string, state: string | undefined) {
    return await new Promise<void>((_resolve, reject) => {
        webAuth.login({ username, password, state, realm: auth0Realm }, (err) => {
            if (err) {
                console.error("Auth0 login error", err);
                reject(new Auth0LoginException(err));
            } else {
                // This promise should never resolve, as the login should redirect
                reject(new Error("Login did not redirect"));
            }
        });
    });
}

export interface GuessedAuth0SignupResult {
    Id?: string;
    email?: string;
    emailVerified?: boolean;
}

async function auth0Signup(webAuth: WebAuth, email: string, password: string) {
    return await new Promise<GuessedAuth0SignupResult>((resolve, reject) => {
        webAuth.signup({ email, password, connection: auth0Realm }, (err, result) => {
            if (err) {
                console.error("Auth0 signup error", err);
                reject(new Auth0SignupException(err));
            } else {
                console.debug("Auth0 signup result", result);
                resolve(result as GuessedAuth0SignupResult);
            }
        });
    });
}

async function auth0ForgotPassword(webAuth: WebAuth, email: string) {
    return await new Promise<unknown>((resolve, reject) => {
        webAuth.changePassword({ email, connection: auth0Realm }, (err, result) => {
            if (err) {
                console.error("Auth0 forgot password error", err);
                reject(new Auth0Exception(err));
            } else {
                console.debug("Auth0 forgot password result", result);
                resolve(result);
            }
        });
    });
}

function auth0SocialLogin(webAuth: WebAuth, connectionId: SocialLoginId, state: string | undefined) {
    webAuth.authorize({ connection: connectionId, state });
}

/**
 * NB: Auth0 logout is currently disabled, as it is not necessary, and requires manually configuring valid logout URLs
 * in the Auth0 settings.
 */
const realLogoutEnabled = false;

function auth0Logout(webAuth: WebAuth, _redirectPath?: string) {
    if (realLogoutEnabled) {
        webAuth.logout({
            returnTo: getFullUrl("/"),
        });
    }
}

async function auth0ChangePassword(webAuth: WebAuth, email: string) {
    return await new Promise<string>((resolve, _reject) => {
        webAuth.changePassword({ email, connection: auth0Realm }, (err, result: string) => {
            if (err) {
                throw new Auth0Exception(err);
            }
            resolve(result);
        });
    });
}

function encodeState(redirectPath: string | undefined) {
    return redirectPath !== undefined ? JSON.stringify(redirectPath) : undefined;
}

export function decodeState(state: string) {
    try {
        const value = JSON.parse(state);
        invariant(typeof value === "string", "Expected string");
        invariant(value.startsWith("/"), "Expected path");
        return value;
    } catch (e) {
        showError("Failed to parse Auth0 state", e);
        return undefined;
    }
}

export const auth0ProviderAtom = atom((get): AuthProvider => {
    // NB: This is just a promise to load auth0, which will be awaited only when a method is called (for lazy loading)
    // Since promises are evaluated eagerly, it preloads the Auth0 library.
    const webAuthP = get(webAuthAtom);
    return {
        signup: async ({ username, password }) => {
            const result = await auth0Signup(await webAuthP, username, password);
            return { email: result.email, emailVerified: !!result.emailVerified };
        },
        login: async ({ username, password, redirectPath }) =>
            await auth0Login(await webAuthP, username, password, encodeState(redirectPath)),
        socialLogin: async ({ redirectPath, connectionId }) =>
            auth0SocialLogin(await webAuthP, connectionId, encodeState(redirectPath)),
        forgotPassword: async ({ email }) => await auth0ForgotPassword(await webAuthP, email),
        changePassword: async ({ email }) => await auth0ChangePassword(await webAuthP, email),
        logout: async ({ redirectPath }) => auth0Logout(await webAuthP, redirectPath),
    };
});
